<script lang="ts">
  import { flip } from 'svelte/animate';
  import { fade } from 'svelte/transition';
  import { onMount } from 'svelte';

  type Rollout = { label: string; name: string; reward: number };

  const initialOrder: Rollout[] = [
    { label: 'A', name: 'Coral Pink', reward: 0.4 },
    { label: 'B', name: 'Royal Red', reward: 0.9 },
    { label: 'C', name: 'Rose Gold', reward: 0.2 },
    { label: 'D', name: 'Ruby Red', reward: 0.8 },
  ];

  const avg = initialOrder.reduce((s, r) => s + r.reward, 0) / initialOrder.length;
  const sortedOrder = [...initialOrder].sort((a, b) => b.reward - a.reward);

  let rollouts = $state<Rollout[]>(initialOrder);
  let showScores = $state(false);
  let showAvg = $state(false);
  let ranked = $state(false);

  const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

  onMount(() => {
    let alive = true;

    const loop = async () => {
      while (alive) {
        // forward (slow, staged)
        await wait(1100);
        if (!alive) break;
        showScores = true;
        await wait(900);
        if (!alive) break;
        showAvg = true;
        await wait(1400);
        if (!alive) break;
        ranked = true;
        rollouts = sortedOrder;
        await wait(2800); // dwell

        // reverse (quick)
        if (!alive) break;
        ranked = false;
        rollouts = initialOrder;
        await wait(600);
        if (!alive) break;
        showAvg = false;
        await wait(300);
        if (!alive) break;
        showScores = false;
        await wait(500);
      }
    };

    loop();
    return () => {
      alive = false;
    };
  });
</script>

<div class="rank">
  <div class="rollouts">
    {#each rollouts as r, i (r.label)}
      <div
        class="row"
        class:above={ranked && r.reward > avg}
        class:below={ranked && r.reward < avg}
        animate:flip={{ duration: 550 }}
      >
        <span class="chip">
          <span class="label">{r.label}</span>
          <span class="sep">·</span>
          <span class="name">{r.name}</span>
        </span>
        {#if showScores}
          <span
            class="reward"
            in:fade={{ duration: 300, delay: i * 140 }}
            out:fade={{ duration: 200 }}
          >
            {r.reward.toFixed(1)}
          </span>
        {/if}
      </div>
    {/each}
  </div>

  <div class="avg" style:opacity={showAvg ? 1 : 0}>
    average: {avg.toFixed(2)}
  </div>
</div>

<style>
  .rank {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    margin: 32px 0;
    font-family: 'Diatype', 'ABC Diatype', Arial, sans-serif;
    color: #333;
  }

  .rollouts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 250px;
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 44px;
    align-items: center;
    gap: 14px;
  }

  .chip {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 7px 12px;
    border: 1px solid #0891b2;
    border-radius: 7px;
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 15px;
    background: transparent;
    color: #0891b2;
    line-height: 1;
    transition: border-color 300ms ease, color 300ms ease;
  }

  .label {
    font-weight: 600;
  }

  .sep {
    color: #a5d8e4;
  }

  .row.above .chip {
    border-color: #28a948;
    color: #28a948;
  }

  .row.above .sep {
    color: #28a948;
    opacity: 0.6;
  }

  .row.below .chip {
    border-color: #e7323f;
    color: #e7323f;
  }

  .row.below .sep {
    color: #e7323f;
    opacity: 0.6;
  }

  .reward {
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    text-align: right;
    color: #0f766e;
    transition: color 300ms ease;
  }

  .row.above .reward {
    color: #28a948;
  }

  .row.below .reward {
    color: #e7323f;
  }

  .avg {
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 13px;
    letter-spacing: 0.04em;
    color: #0f766e;
    transition: opacity 300ms ease;
  }
</style>
